import * as React from 'react';

import {
  RateCollectionApiError,
  type RateSeniorityLevelDto,
} from '@/api/rate-collection-client';
import type { AutosaveCellVariables } from '@/api/use-rate-collection';
import { cellKey } from '@/routes/supplier/rate-collection/saisie-model';

// =============================================================================
// use-saisie-autosave.ts — Moteur d'AUTOSAVE silencieux de la grille (S12, D2).
// =============================================================================
// Tient en MÉMOIRE APPLICATIVE (React state, ZÉRO localStorage — SF §503 / DBU §1.4.7)
// l'état d'édition de CHAQUE cellule : brouillon courant, dernière valeur enregistrée
// serveur, horodatage `lastSavedAt`, statut (idle/saving/saved/error/invalid). Déclencheurs
// d'autosave (POINT 3) :
//   1. PERTE DE FOCUS d'une cellule modifiée (flushCell) ;
//   2. INTERVALLE de 2 minutes (flush de toutes les cellules « dirty ») ;
//   3. RETRY manuel après échec (la valeur saisie n'est JAMAIS perdue).
// L'appel serveur est l'UPSERT IDEMPOTENT (PUT) — re-flush de la même cellule = pas de doublon.
//
// RÉSILIENCE (ST-SAISIE-02) : un échec réseau passe la cellule en `error` SANS perdre le
// brouillon (mémoire applicative). La récupération « propre » après coupure relève du re-fetch
// serveur du Brouillon persisté (hook useGrille au remontage) — JAMAIS d'une restauration locale.

const AUTOSAVE_INTERVAL_MS = 2 * 60 * 1000;

/** Statut d'autosave d'une cellule (porteur d'UX + a11y, jamais la couleur seule). */
export type CellSaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error' | 'invalid';

/** État d'édition d'une cellule en mémoire applicative. */
export interface CellEditState {
  /** Valeur brouillon courante (chaîne du champ, peut être vide pendant la frappe). */
  readonly draft: string;
  /** Dernière valeur ENREGISTRÉE serveur (source de vérité d'affichage hors édition). */
  readonly savedValue: number | null;
  /** Horodatage serveur de la dernière sauvegarde (ISO 8601) ou `null`. */
  readonly lastSavedAt: string | null;
  readonly status: CellSaveStatus;
  /** Message d'erreur de validation/réseau (affiché inline + aria). */
  readonly error: string | null;
}

/** Graine d'initialisation d'une cellule (depuis la vue serveur). */
export interface CellSeed {
  readonly profileCode: string;
  readonly seniorityLevel: RateSeniorityLevelDto;
  readonly savedValue: number | null;
  readonly lastSavedAt: string | null;
}

/** Surface du hook consommée par l'écran. */
export interface SaisieAutosave {
  /** Lit l'état d'édition d'une cellule (jamais `undefined` : seed paresseux). */
  getCell: (profileCode: string, seniorityLevel: RateSeniorityLevelDto) => CellEditState;
  /** Met à jour le brouillon (frappe) — VALIDE inline (entier>0) sans appel serveur. */
  setDraft: (
    profileCode: string,
    seniorityLevel: RateSeniorityLevelDto,
    draft: string,
  ) => void;
  /** Déclenche l'autosave d'UNE cellule si elle est « dirty » (appelé sur blur). */
  flushCell: (profileCode: string, seniorityLevel: RateSeniorityLevelDto) => void;
  /**
   * SF1 (DBU §3.5 l.2863) — « Sauvegarder » manuel : flush de TOUTES les cellules « dirty »/en erreur.
   * Idempotent (mêmes gardes que flushCell : les cellules au repos ne re-PUT pas). Ne perd aucune saisie.
   */
  flushAll: () => void;
  /** Re-tente l'autosave d'une cellule en erreur (la valeur saisie est conservée). */
  retryCell: (profileCode: string, seniorityLevel: RateSeniorityLevelDto) => void;
  /** Vrai si au moins une cellule est en cours d'enregistrement (indicateur global). */
  readonly isSaving: boolean;
  /** Vrai si au moins une cellule est en échec (indicateur global + retry). */
  readonly hasError: boolean;
  /** Horodatage serveur le plus récent toutes cellules confondues (indicateur « enregistré à … »). */
  readonly lastSavedAt: string | null;
}

/** Valide un brouillon : entier strictement positif. Renvoie le nombre, ou `null` si invalide. */
export function parseTjmDraft(draft: string): number | null {
  const trimmed = draft.trim();
  if (trimmed === '' || !/^\d+$/.test(trimmed)) {
    return null;
  }
  const value = Number.parseInt(trimmed, 10);
  return Number.isInteger(value) && value > 0 ? value : null;
}

const INVALID_MESSAGE = 'Le TJM doit être un entier strictement positif (en euros).';

/**
 * Moteur d'autosave. `mutateAsync` = la mutation TanStack (PUT idempotent) ; `locked` = la
 * saisie est verrouillée (submission Soumis) → aucun flush. `onSavedAck` notifie l'écran de
 * l'ack (ex. pour rafraîchir l'état de submission). Aucune dépendance localStorage.
 */
export function useSaisieAutosave(params: {
  readonly seeds: readonly CellSeed[];
  readonly locked: boolean;
  readonly mutateAsync: (vars: AutosaveCellVariables) => Promise<{
    readonly lastSavedAt: string;
    readonly submissionState: string;
  }>;
  readonly onSavedAck?: (submissionState: string) => void;
  /**
   * S13 : notifie l'écran d'une erreur applicative d'autosave (ex. deadline dépassée). Permet le
   * lock-out deadline (G0-S13-3) sans coupler le moteur d'autosave aux règles d'UI de l'écran.
   */
  readonly onSaveError?: (error: RateCollectionApiError) => void;
}): SaisieAutosave {
  const { seeds, locked, mutateAsync, onSavedAck, onSaveError } = params;

  // État d'édition en MÉMOIRE (jamais persisté localement). Seedé depuis la vue serveur.
  const [cells, setCells] = React.useState<ReadonlyMap<string, CellEditState>>(() =>
    seedMap(seeds),
  );

  // Re-seed quand la vue serveur change (1er chargement / re-fetch après coupure) — mais
  // SANS écraser un brouillon en cours d'édition (dirty/saving/error) : la mémoire applicative
  // prime tant que l'utilisateur n'a pas perdu sa saisie.
  const seedKey = React.useMemo(() => stableSeedKey(seeds), [seeds]);
  React.useEffect(() => {
    setCells((prev) => mergeSeed(prev, seeds));
    // seedKey condense les seeds : on ne re-seed que sur changement réel de la vue serveur.
  }, [seedKey, seeds]);

  const cellsRef = React.useRef(cells);
  cellsRef.current = cells;

  const updateCell = React.useCallback(
    (key: string, patch: Partial<CellEditState>): void => {
      setCells((prev) => {
        const current = prev.get(key) ?? blankCell();
        const next = new Map(prev);
        next.set(key, { ...current, ...patch });
        return next;
      });
    },
    [],
  );

  const doFlush = React.useCallback(
    async (
      profileCode: string,
      seniorityLevel: RateSeniorityLevelDto,
    ): Promise<void> => {
      const key = cellKey(profileCode, seniorityLevel);
      const cell = cellsRef.current.get(key);
      if (cell === undefined) {
        return;
      }
      // Rien à enregistrer si non « dirty » (ou en cours). Idempotence côté client.
      if (cell.status !== 'dirty' && cell.status !== 'error') {
        return;
      }
      const value = parseTjmDraft(cell.draft);
      if (value === null) {
        // Brouillon vide = pas d'effacement serveur (S12 : pas de DELETE de cellule).
        updateCell(
          key,
          cell.draft.trim() === ''
            ? { status: 'idle', error: null }
            : { status: 'invalid', error: INVALID_MESSAGE },
        );
        return;
      }
      updateCell(key, { status: 'saving', error: null });
      try {
        const ack = await mutateAsync({
          profileCode,
          seniorityLevel,
          input: { tjmValueEur: value },
        });
        updateCell(key, {
          status: 'saved',
          savedValue: value,
          lastSavedAt: ack.lastSavedAt,
          error: null,
        });
        onSavedAck?.(ack.submissionState);
      } catch (err) {
        const message =
          err instanceof RateCollectionApiError
            ? err.message
            : 'Échec de l’enregistrement (réseau). La saisie est conservée.';
        // VALEUR CONSERVÉE en mémoire : status error + retry possible (résilience coupure).
        updateCell(key, { status: 'error', error: message });
        // S13 : remonter l'erreur applicative typée (lock-out deadline, etc.) à l'écran.
        if (err instanceof RateCollectionApiError) {
          onSaveError?.(err);
        }
      }
    },
    [mutateAsync, onSavedAck, onSaveError, updateCell],
  );

  // Déclencheur 2 : intervalle 2 min → flush de toutes les cellules « dirty » (ou en erreur).
  React.useEffect(() => {
    if (locked) {
      return;
    }
    const id = setInterval(() => {
      for (const [key, cell] of cellsRef.current) {
        if (cell.status === 'dirty' || cell.status === 'error') {
          const [profileCode, seniorityLevel] = splitKey(key);
          void doFlush(profileCode, seniorityLevel);
        }
      }
    }, AUTOSAVE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [doFlush, locked]);

  const getCell = React.useCallback(
    (profileCode: string, seniorityLevel: RateSeniorityLevelDto): CellEditState =>
      cells.get(cellKey(profileCode, seniorityLevel)) ?? blankCell(),
    [cells],
  );

  const setDraft = React.useCallback(
    (
      profileCode: string,
      seniorityLevel: RateSeniorityLevelDto,
      draft: string,
    ): void => {
      if (locked) {
        return;
      }
      const key = cellKey(profileCode, seniorityLevel);
      const value = parseTjmDraft(draft);
      const isEmpty = draft.trim() === '';
      updateCell(key, {
        draft,
        status: isEmpty ? 'idle' : value === null ? 'invalid' : 'dirty',
        error: !isEmpty && value === null ? INVALID_MESSAGE : null,
      });
    },
    [locked, updateCell],
  );

  const flushCell = React.useCallback(
    (profileCode: string, seniorityLevel: RateSeniorityLevelDto): void => {
      if (locked) {
        return;
      }
      void doFlush(profileCode, seniorityLevel);
    },
    [doFlush, locked],
  );

  const retryCell = React.useCallback(
    (profileCode: string, seniorityLevel: RateSeniorityLevelDto): void => {
      if (locked) {
        return;
      }
      void doFlush(profileCode, seniorityLevel);
    },
    [doFlush, locked],
  );

  // SF1 — « Sauvegarder » manuel : parcourt l'état courant (ref) et flush chaque cellule à écrire.
  const flushAll = React.useCallback((): void => {
    if (locked) {
      return;
    }
    for (const [key, cell] of cellsRef.current) {
      if (cell.status === 'dirty' || cell.status === 'error') {
        const [profileCode, seniorityLevel] = splitKey(key);
        void doFlush(profileCode, seniorityLevel);
      }
    }
  }, [doFlush, locked]);

  const { isSaving, hasError, lastSavedAt } = React.useMemo(() => {
    let saving = false;
    let error = false;
    let latest: string | null = null;
    for (const cell of cells.values()) {
      if (cell.status === 'saving') {
        saving = true;
      }
      if (cell.status === 'error') {
        error = true;
      }
      if (cell.lastSavedAt !== null && (latest === null || cell.lastSavedAt > latest)) {
        latest = cell.lastSavedAt;
      }
    }
    return { isSaving: saving, hasError: error, lastSavedAt: latest };
  }, [cells]);

  return {
    getCell,
    setDraft,
    flushCell,
    flushAll,
    retryCell,
    isSaving,
    hasError,
    lastSavedAt,
  };
}

// --- Helpers purs ----------------------------------------------------------

function blankCell(): CellEditState {
  return { draft: '', savedValue: null, lastSavedAt: null, status: 'idle', error: null };
}

function seedCell(seed: CellSeed): CellEditState {
  return {
    draft: seed.savedValue !== null ? String(seed.savedValue) : '',
    savedValue: seed.savedValue,
    lastSavedAt: seed.lastSavedAt,
    status: 'idle',
    error: null,
  };
}

function seedMap(seeds: readonly CellSeed[]): ReadonlyMap<string, CellEditState> {
  const map = new Map<string, CellEditState>();
  for (const seed of seeds) {
    map.set(cellKey(seed.profileCode, seed.seniorityLevel), seedCell(seed));
  }
  return map;
}

/**
 * Fusionne une nouvelle graine serveur dans l'état courant. Une cellule en cours d'édition
 * (dirty/saving/error/invalid) est PRÉSERVÉE (la mémoire applicative prime — pas d'écrasement
 * de la saisie en cours). Les cellules au repos (idle) adoptent la valeur serveur (re-fetch
 * après coupure : la grille reflète le Brouillon PERSISTÉ).
 */
function mergeSeed(
  prev: ReadonlyMap<string, CellEditState>,
  seeds: readonly CellSeed[],
): ReadonlyMap<string, CellEditState> {
  const next = new Map(prev);
  for (const seed of seeds) {
    const key = cellKey(seed.profileCode, seed.seniorityLevel);
    const current = prev.get(key);
    if (current === undefined || current.status === 'idle' || current.status === 'saved') {
      next.set(key, seedCell(seed));
    }
  }
  return next;
}

/** Condense les seeds en une clé stable (re-seed seulement sur changement réel). */
function stableSeedKey(seeds: readonly CellSeed[]): string {
  return seeds
    .map((s) => `${cellKey(s.profileCode, s.seniorityLevel)}=${String(s.savedValue)}@${String(s.lastSavedAt)}`)
    .join('|');
}

function splitKey(key: string): [string, RateSeniorityLevelDto] {
  const idx = key.lastIndexOf('::');
  return [key.slice(0, idx), key.slice(idx + 2) as RateSeniorityLevelDto];
}
